import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { statsAPI, communitiesAPI, startupsAPI, eventsAPI, newsletterAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  Users, 
  Rocket, 
  Calendar, 
  Briefcase,
  ChevronRight,
  ExternalLink
} from 'lucide-react';

export const HomePage = () => {
  const [stats, setStats] = useState({ communities: 0, startups: 0, events: 0, jobs: 0 });
  const [featuredCommunities, setFeaturedCommunities] = useState([]);
  const [featuredStartups, setFeaturedStartups] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, communitiesRes, startupsRes, eventsRes] = await Promise.all([
          statsAPI.get(),
          communitiesAPI.getAll({ featured: true }),
          startupsAPI.getAll({ featured: true }),
          eventsAPI.getAll({ from_date: new Date().toISOString().split('T')[0] })
        ]);
        setStats(statsRes.data);
        setFeaturedCommunities(communitiesRes.data.slice(0, 4));
        setFeaturedStartups(startupsRes.data.slice(0, 6));
        setUpcomingEvents(eventsRes.data.slice(0, 4));
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await newsletterAPI.subscribe(email);
      toast.success('Successfully subscribed!');
      setEmail('');
    } catch (err) {
      toast.error('Failed to subscribe');
    }
  };

  const categories = [
    { name: 'AI & ML', slug: 'ai-ml', icon: '◈', count: 12 },
    { name: 'Robotics', slug: 'robotics', icon: '◉', count: 8 },
    { name: 'Creative Tech', slug: 'creative', icon: '◎', count: 15 },
    { name: 'Startups', slug: 'startup', icon: '◇', count: 24 },
    { name: 'Diversity', slug: 'diversity', icon: '◆', count: 10 },
    { name: 'SaaS', slug: 'saas', icon: '□', count: 18 },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="border-b border-border">
        <div className="container-wide py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="animate-slide-up">
              <span className="text-label text-muted-foreground mb-4 block">
                Portland, Oregon
              </span>
              <h1 className="text-display text-4xl sm:text-5xl lg:text-7xl mb-6">
                Discover<br />Portland<br />Tech
              </h1>
              <p className="text-lg text-muted-foreground max-w-md mb-8">
                The comprehensive guide to Portland's technology ecosystem. 
                Discover communities, startups, events, and opportunities.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/communities">
                  <Button size="lg" className="h-12 px-6" data-testid="explore-communities-btn">
                    Explore Communities
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/events">
                  <Button variant="outline" size="lg" className="h-12 px-6" data-testid="view-events-btn">
                    View Events
                  </Button>
                </Link>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-6 animate-slide-up delay-200">
              <div className="stat-card min-h-[140px]">
                <div className="stat-value">{stats.communities || '—'}</div>
                <div className="stat-label">Communities</div>
              </div>
              <div className="stat-card min-h-[140px]">
                <div className="stat-value">{stats.startups || '—'}</div>
                <div className="stat-label">Startups</div>
              </div>
              <div className="stat-card min-h-[140px]">
                <div className="stat-value">{stats.events || '—'}</div>
                <div className="stat-label">Events</div>
              </div>
              <div className="stat-card min-h-[140px]">
                <div className="stat-value">{stats.jobs || '—'}</div>
                <div className="stat-label">Open Jobs</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="border-b border-border">
        <div className="container-wide pt-16 pb-20">
          <div className="flex items-center justify-between mb-10">
            <h2 className="text-heading text-2xl">Browse by Category</h2>
            <Link to="/communities" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
              View all <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat, index) => (
              <Link 
                key={cat.slug}
                to={`/communities?category=${cat.slug}`}
                className="border border-border p-8 hover:border-primary/50 transition-colors group animate-slide-up"
                style={{ animationDelay: `${index * 50}ms` }}
                data-testid={`category-${cat.slug}`}
              >
                <span className="text-2xl mb-4 block opacity-50 group-hover:opacity-100 transition-opacity">
                  {cat.icon}
                </span>
                <h3 className="font-semibold text-sm mb-2">{cat.name}</h3>
                <span className="text-xs text-muted-foreground">{cat.count} groups</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Communities */}
      {featuredCommunities.length > 0 && (
        <section className="border-b border-border">
          <div className="container-wide pt-16 pb-20">
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-heading text-2xl">Featured Communities</h2>
              <Link to="/communities" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
                View all <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {featuredCommunities.map((community, index) => (
                <Link 
                  key={community.id}
                  to={`/communities/${community.id}`}
                  className="group"
                >
                  <Card className="card-hover h-full">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-sm bg-muted flex items-center justify-center">
                          <Users className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <span className="badge-category">{community.category}</span>
                      </div>
                      <h3 className="font-semibold mb-2 group-hover:text-primary transition-colors">
                        {community.name}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {community.description}
                      </p>
                      {community.member_count && (
                        <p className="text-xs text-muted-foreground mt-3">
                          {community.member_count.toLocaleString()} members
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <section className="border-b border-border">
          <div className="container-wide pt-16 pb-20">
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-heading text-2xl">Upcoming Events</h2>
              <Link to="/events" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
                View calendar <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="divide-y divide-border border border-border">
              {upcomingEvents.map(event => (
                <Link 
                  key={event.id}
                  to={`/events/${event.id}`}
                  className="flex items-center gap-6 p-6 hover:bg-muted/50 transition-colors"
                  data-testid={`event-${event.id}`}
                >
                  <div className="text-center min-w-[60px]">
                    <div className="text-2xl font-bold">
                      {new Date(event.date).getDate()}
                    </div>
                    <div className="text-xs text-muted-foreground uppercase">
                      {new Date(event.date).toLocaleDateString('en-US', { month: 'short' })}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="badge-category">{event.event_type}</span>
                      {event.is_virtual && (
                        <span className="text-xs text-muted-foreground">Virtual</span>
                      )}
                    </div>
                    <h3 className="font-semibold truncate">{event.title}</h3>
                    <p className="text-sm text-muted-foreground truncate">
                      {event.location}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Startups */}
      {featuredStartups.length > 0 && (
        <section className="border-b border-border">
          <div className="container-wide pt-16 pb-20">
            <div className="flex items-center justify-between mb-10">
              <h2 className="text-heading text-2xl">Featured Startups</h2>
              <Link to="/startups" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
                View directory <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featuredStartups.map(startup => (
                <Link 
                  key={startup.id}
                  to={`/startups/${startup.id}`}
                  className="group"
                >
                  <Card className="card-hover h-full">
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="w-12 h-12 rounded-sm bg-muted flex items-center justify-center">
                          <Rocket className="w-6 h-6 text-muted-foreground" />
                        </div>
                        {startup.is_hiring && (
                          <span className="text-xs bg-green-500/10 text-green-600 dark:text-green-400 px-2 py-1 rounded-full">
                            Hiring
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                        {startup.name}
                      </h3>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="badge-category">{startup.industry}</span>
                        <span className="text-xs text-muted-foreground">{startup.stage}</span>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {startup.description}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter Section */}
      <section className="border-b border-border bg-muted/30">
        <div className="container-wide py-20">
          <div className="max-w-2xl mx-auto text-center">
            <span className="text-label text-muted-foreground mb-4 block">
              Stay Connected
            </span>
            <h2 className="text-heading text-3xl mb-4">
              Portland Tech Newsletter
            </h2>
            <p className="text-muted-foreground mb-8">
              Get weekly updates on Portland's tech ecosystem. New startups, 
              upcoming events, job opportunities, and community news.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 h-12"
                data-testid="newsletter-email-input"
              />
              <Button type="submit" size="lg" className="h-12 px-6" data-testid="newsletter-submit-btn">
                Subscribe
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section>
        <div className="container-wide pt-16 pb-20">
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/get-involved" className="group">
              <Card className="card-hover invert-hover h-full">
                <CardContent className="p-8">
                  <h3 className="text-heading text-xl mb-2">Get Involved</h3>
                  <p className="text-muted-foreground text-sm group-hover:text-primary-foreground/70">
                    Join the community, attend events, and contribute to Portland's tech scene.
                  </p>
                  <ArrowRight className="w-5 h-5 mt-4" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/about" className="group">
              <Card className="card-hover invert-hover h-full">
                <CardContent className="p-8">
                  <h3 className="text-heading text-xl mb-2">About Portland Tech</h3>
                  <p className="text-muted-foreground text-sm group-hover:text-primary-foreground/70">
                    Learn about the history and impact of technology in Portland.
                  </p>
                  <ArrowRight className="w-5 h-5 mt-4" />
                </CardContent>
              </Card>
            </Link>
            <Link to="/contact" className="group">
              <Card className="card-hover invert-hover h-full">
                <CardContent className="p-8">
                  <h3 className="text-heading text-xl mb-2">Contact & Partnerships</h3>
                  <p className="text-muted-foreground text-sm group-hover:text-primary-foreground/70">
                    Connect with us for sponsorships, collaborations, and inquiries.
                  </p>
                  <ArrowRight className="w-5 h-5 mt-4" />
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
